import React, { FC, useMemo } from 'react';
import { FaBullhorn, FaCreditCard, FaGlobe, FaRegFileAlt } from 'react-icons/fa';
import { matchPath, useHistory, useLocation } from 'react-router-dom';
import { TabItem, Tabs } from '@/lib';
import { RouteConstants } from '@/features/router/RouteConstants';
import { EMAIL_CONNECTOR_NAME } from '@/features/campaigns/EmailCampaign/constants';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useCompanyValues } from '@/providers';
import { useGetCampaignsCountV2Query } from '@/services/endpoints/campaigns';
import { useGetWorkspaceConnectorsQuery } from '@/services/endpoints/workspace-connectors';
import { EmailCampaignProvider } from '../context/EmailCampaignProvider';
import { useEmailTemplates } from '../hooks/useEmailTemplates';
import { CampaignsEmailRoutesV2 } from './CampaignsEmailRoutesV2';

const EMAIL_CAMPAIGNS_PATTERNS = [
  RouteConstants.emailCampaigns,
  RouteConstants.createCampaignEmail,
  RouteConstants.editCampaignEmail,
  RouteConstants.emailCampaignDetails,
];

const EMAIL_TEMPLATES_PATTERNS = [
  RouteConstants.emailTemplates,
  RouteConstants.createEmailTemplate,
  RouteConstants.editEmailTemplate,
  RouteConstants.emailTemplateDetails,
  RouteConstants.emailTemplatesEditHub,
  RouteConstants.emailTemplatesDeleteHub,
];

const EMAIL_PACKAGES_PATTERNS = [RouteConstants.emailPackages];

const EMAIL_DOMAINS_PATTERNS = [RouteConstants.emailDomains];

const EmailWorkspaceInit = () => {
  const history = useHistory();
  const location = useLocation();
  const workspace = useWorkspace();
  const { currentLanguage } = useCompanyValues();

  const { data: customConnectors, isLoading: isConnectorsLoading } = useGetWorkspaceConnectorsQuery(
    { workspaceId: workspace.id, lang: currentLanguage },
    { skip: !workspace.id }
  );
  const isMailtrapConnected = customConnectors?.find((connector) => connector.name === EMAIL_CONNECTOR_NAME)?.isAuthenticated ?? false;

  const { data: campaignsCount } = useGetCampaignsCountV2Query({ workspaceId: workspace.id, providerType: 'EMAIL' });
  const { templatesCount, isLoading: isTemplatesLoading } = useEmailTemplates({ skip: !isMailtrapConnected });
  const templatesTabCount = isConnectorsLoading || !isMailtrapConnected || isTemplatesLoading ? 'N/A' : templatesCount;

  const tabItems = useMemo<TabItem[]>(
    () => [
      {
        id: 'campaigns',
        title: 'campaign.whatsappWorkspace.campaigns' as const,
        icon: <FaBullhorn className='size-4' />,
        count: campaignsCount ?? 'N/A',
        element: <CampaignsEmailRoutesV2 />,
      },
      {
        id: 'templates',
        title: 'campaign.whatsappWorkspace.templates' as const,
        icon: <FaRegFileAlt className='size-4' />,
        count: templatesTabCount,
        element: <CampaignsEmailRoutesV2 />,
      },
      {
        id: 'packages',
        title: 'campaign.whatsappWorkspace.packages' as const,
        icon: <FaCreditCard className='size-4' />,
        count: 4,
        element: <CampaignsEmailRoutesV2 />,
      },
      {
        id: 'domains',
        title: 'campaign.emailWorkspace.domainsTab' as const,
        icon: <FaGlobe className='size-4' />,
        element: <CampaignsEmailRoutesV2 />,
      },
    ],
    [campaignsCount, templatesTabCount]
  );

  const activeTabId = useMemo(() => {
    const isCampaignRoute = EMAIL_CAMPAIGNS_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isCampaignRoute) {
      return 'campaigns';
    }

    const isTemplateRoute = EMAIL_TEMPLATES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isTemplateRoute) {
      return 'templates';
    }

    const isPackageRoute = EMAIL_PACKAGES_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isPackageRoute) {
      return 'packages';
    }

    const isDomainsRoute = EMAIL_DOMAINS_PATTERNS.some((pattern) => matchPath(location.pathname, { path: pattern, exact: true }));
    if (isDomainsRoute) {
      return 'domains';
    }

    return 'campaigns';
  }, [location.pathname]);

  const currentTab = tabItems.find((item) => item.id === activeTabId) ?? tabItems[0];

  const handleSelectTab = (tabItem: TabItem) => {
    if (tabItem.id === 'campaigns') {
      history.push(RouteConstants.emailCampaigns);
      return;
    }

    if (tabItem.id === 'templates') {
      history.push(RouteConstants.emailTemplates);
      return;
    }

    if (tabItem.id === 'packages') {
      history.push(RouteConstants.emailPackages);
      return;
    }

    if (tabItem.id === 'domains') {
      history.push(RouteConstants.emailDomains);
    }
  };

  return (
    <div className='w-full grid grid-cols-1'>
      <Tabs items={tabItems} currentTab={currentTab.title} onSelectTab={handleSelectTab} />
    </div>
  );
};

export const CampaignsEmailWorkspaceV2: FC = () => {
  return (
    <EmailCampaignProvider>
      <EmailWorkspaceInit />
    </EmailCampaignProvider>
  );
};
